/**
 * Brief notes → hotel preferences (XT-806).
 *
 * The request's free-text notes ("a quiet place with a spa, no kids") become a
 * short list of preference keys from a fixed vocabulary; a hotel's own words
 * (title, description, subcategory, hashtags, facilities, outdoor facilities,
 * stars) say which of them it meets. Deterministic — no model.
 *
 * Limits, by design: English notes, simple negation only ("no", "not",
 * "without", "avoid" up to four words before the term, or a refusal after it
 * — "not needed", "are not accepted" — within the same clause). The same
 * rule reads the hotel's own text.
 */

import { fold } from './brief';

export type HotelPreference =
  | 'spa'
  | 'pool'
  | 'beach'
  | 'sea-view'
  | 'boutique'
  | 'family'
  | 'adults-only'
  | 'golf'
  | 'wine'
  | 'quiet'
  | 'rooftop'
  | 'gym'
  | 'terrace'
  | 'garden'
  | 'luxury';

type VocabEntry = {
  readonly key: HotelPreference;
  /** Phrases in the notes that ask for it. */
  readonly note: readonly string[];
  /** Words in a hotel's own text that meet it. */
  readonly match: readonly string[];
};

/** Vocabulary order is the output order. */
export const HOTEL_PREFERENCE_VOCAB: readonly VocabEntry[] = [
  {
    key: 'spa',
    note: ['spa', 'wellness', 'massage', 'sauna'],
    match: ['spa', 'wellness', 'massage', 'sauna', 'hammam'],
  },
  {
    key: 'pool',
    note: ['pool', 'swimming'],
    match: ['pool', 'piscina', 'swimming'],
  },
  {
    key: 'beach',
    note: ['beach', 'beachfront', 'seaside'],
    match: ['beach', 'beachfront', 'seaside', 'praia'],
  },
  {
    key: 'sea-view',
    note: ['sea view', 'sea views', 'ocean view', 'ocean views'],
    match: ['sea view', 'ocean view', 'seaview', 'oceanview', 'oceanfront'],
  },
  {
    key: 'boutique',
    note: ['boutique', 'charming', 'intimate'],
    match: ['boutique', 'charming', 'intimate'],
  },
  {
    key: 'family',
    note: ['family friendly', 'family hotel', 'kids club', 'family suite'],
    match: ['family', 'kids', 'children', 'kids club'],
  },
  {
    key: 'adults-only',
    // "no kids" / "no children" carry their own negation (`selfNegating`).
    note: ['adults only', 'adult only', 'child free', 'no kids', 'no children'],
    match: ['adults only', 'adult only', 'adultsonly'],
  },
  // Word-bounded: "golfinhos" (dolphins) never reads as golf (ITB01-19).
  { key: 'golf', note: ['golf'], match: ['golf'] },
  {
    key: 'wine',
    note: ['wine', 'vineyard', 'vineyards', 'winery'],
    match: ['wine', 'vineyard', 'winery', 'vinho', 'quinta'],
  },
  {
    key: 'quiet',
    note: ['quiet', 'peaceful', 'tranquil', 'secluded', 'calm'],
    match: ['quiet', 'peaceful', 'tranquil', 'secluded', 'seclusion', 'calm'],
  },
  {
    key: 'rooftop',
    note: ['rooftop', 'roof terrace'],
    match: ['rooftop', 'roof terrace'],
  },
  { key: 'gym', note: ['gym', 'fitness'], match: ['gym', 'fitness'] },
  {
    key: 'terrace',
    note: ['terrace', 'balcony'],
    match: ['terrace', 'balcony'],
  },
  { key: 'garden', note: ['garden', 'gardens'], match: ['garden'] },
  {
    key: 'luxury',
    note: ['luxury', 'luxurious', '5 star', 'five star'],
    match: ['luxury', 'luxurious'],
  },
];

export const HOTEL_PREFERENCE_KEYS: readonly HotelPreference[] =
  HOTEL_PREFERENCE_VOCAB.map((entry) => entry.key);

const MAX_PREFERENCES = 12;
/** Hits past this many no longer move a hotel further. */
export const HOTEL_PREFERENCE_MAX_HITS = 3;

const NEGATIONS = new Set(['no', 'not', 'without', 'avoid', 'avoiding']);
/**
 * A hotel's own text may be Portuguese, where "no" means "in the" ("no centro
 * com piscina"): there "no" negates only right before the term ("no
 * children"); the unambiguous words reach back as in the notes.
 */
const HOTEL_NEGATIONS = new Set(['not', 'without', 'avoid', 'avoiding']);
const HOTEL_ADJACENT_NEGATIONS = new Set(['no']);

/** How a mention is read: the notes' way, or a hotel's own text. */
type Reading = {
  /** Words that negate the term up to four words after them. */
  readonly negations: ReadonlySet<string>;
  /** Words that negate only the term right after them. */
  readonly adjacentNegations: ReadonlySet<string>;
  /** Accept the last word's plural ("pools", "gardens", "balconies"). */
  readonly plural: boolean;
};

const NOTES_READING: Reading = {
  negations: NEGATIONS,
  adjacentNegations: new Set(),
  plural: false,
};
const HOTEL_READING: Reading = {
  negations: HOTEL_NEGATIONS,
  adjacentNegations: HOTEL_ADJACENT_NEGATIONS,
  plural: true,
};

/**
 * Folded, punctuation-light text: "Sea-View" and "sea view" read the same.
 * A line break ends a clause ("No kids⏎Pool please" asks for both), so it
 * becomes a full stop before spaces collapse.
 */
const plain = (value: string): string =>
  fold(value)
    .replace(/[-_/]+/g, ' ')
    .replace(/\s*[\r\n]+\s*/g, '. ')
    .replace(/\s+/g, ' ');

const escapeRe = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** A word and its plural: "pool(s)", "terrace(s)", "balcon(y|ies)". */
const pluralRe = (word: string): string =>
  /[^aeiou]y$/.test(word)
    ? `${escapeRe(word.slice(0, -1))}(?:y|ies)`
    : `${escapeRe(word)}(?:e?s)?`;

/**
 * Whole-phrase matcher; internal spaces accept any run of spaces. With
 * `plural`, the last word may also be in its plural.
 */
const phraseRe = (phrase: string, plural = false): RegExp => {
  const words = plain(phrase).split(' ');
  const body = words
    .map((word, at) =>
      plural && at === words.length - 1 ? pluralRe(word) : escapeRe(word)
    )
    .join('\\s+');
  return new RegExp(`(?<![\\p{L}\\p{N}])${body}(?![\\p{L}\\p{N}])`, 'giu');
};

const CLAUSE_BREAK = /[,.;:!?\n]|\bbut\b/u;

/**
 * "children (under 16) are not accepted", "pool not available". Words in
 * between may carry brackets; `after` is already cut at the clause break.
 */
const TRAILING_NEGATION =
  /^(?:\s+\S+){0,3}?\s+(?:is\s+|are\s+)?(?:not|never)\s+(?:needed|required|wanted|necessary|accepted|allowed|permitted|available|welcome)(?![\p{L}])/u;

/** "not far from", "not too far from", "no more than 5 min from" mean near. */
const NEAR_AFTER_NEGATION = new Set(['far', 'further', 'farther', 'more']);

const negatesAhead = (
  words: readonly string[],
  at: number,
  negations: ReadonlySet<string>
): boolean => {
  if (!negations.has(words[at] ?? '')) return false;
  const next = words[at + 1] ?? '';
  if (NEAR_AFTER_NEGATION.has(next)) return false;
  return !(next === 'too' && NEAR_AFTER_NEGATION.has(words[at + 2] ?? ''));
};

/**
 * Whether the mention at `index` is negated, within its own clause only
 * ("no kids, a pool" still asks for a pool): up to four words before it
 * ("no pool", "avoid golf" — but "not far from the beach" is near it), or a
 * refusal after it ("children are not accepted").
 */
const negatedAt = (
  text: string,
  index: number,
  length: number,
  reading: Reading
): boolean => {
  const start = Math.max(0, index - 40);
  let window = text.slice(start, index);
  // A window that starts mid-word ("casi|no") must not read its tail as "no".
  if (start > 0 && /[\p{L}']/u.test(text[start - 1] ?? '')) {
    window = window.replace(/^[\p{L}']+/u, '');
  }
  const before = (window.split(CLAUSE_BREAK).pop() ?? '')
    .split(/[^\p{L}']+/u)
    .filter(Boolean)
    .slice(-4);
  if (before.some((_, at) => negatesAhead(before, at, reading.negations))) {
    return true;
  }
  if (reading.adjacentNegations.has(before[before.length - 1] ?? '')) {
    return true;
  }
  const after =
    text.slice(index + length, index + length + 60).split(CLAUSE_BREAK)[0] ??
    '';
  return TRAILING_NEGATION.test(after);
};

/** A phrase that opens with a negation ("no kids") is itself the ask. */
const selfNegating = (phrase: string): boolean =>
  NEGATIONS.has(plain(phrase).split(' ')[0] ?? '');

/** "no kids club", "no children's menu": the negation is of the facility. */
const COMPOUND_NOUN_AFTER =
  /^'?s?\s+(?:club|clubs|menu|menus|pool|pools|area|areas|activity|activities|program|programs|programme|programmes|room|rooms|corner|zone|zones)(?![\p{L}])/u;

/** True when `phrase` occurs at least once without being negated. */
const mentions = (
  text: string,
  phrase: string,
  reading: Reading = NOTES_READING
): boolean => {
  for (const hit of text.matchAll(phraseRe(phrase, reading.plural))) {
    const index = hit.index ?? 0;
    const end = index + hit[0].length;
    if (selfNegating(phrase)) {
      if (!COMPOUND_NOUN_AFTER.test(text.slice(end, end + 30))) return true;
      continue;
    }
    if (!negatedAt(text, index, hit[0].length, reading)) return true;
  }
  return false;
};

const asked = (text: string, entry: VocabEntry): boolean =>
  entry.note.some((phrase) => mentions(text, phrase));

/** The hotel preferences a request's notes ask for, in vocabulary order. */
export const hotelPreferencesFromNotes = (
  notes: string | null | undefined
): HotelPreference[] => {
  const text = plain(notes ?? '');
  if (!text.trim()) return [];
  return HOTEL_PREFERENCE_VOCAB.filter((entry) => asked(text, entry))
    .map((entry) => entry.key)
    .slice(0, MAX_PREFERENCES);
};

/** What a hotel says about itself. Every field is optional. */
export type HotelFacts = {
  readonly title?: string | null;
  readonly description?: string | null;
  readonly subcategory?: string | null;
  readonly hashtags?: readonly string[] | null;
  readonly facilities?: readonly string[] | null;
  readonly outdoorFacilities?: readonly string[] | null;
  readonly stars?: number | null;
};

const compact = (value: string): string =>
  plain(value).replace(/^#/, '').replace(/\s+/g, '');

/** A compact hashtag term and its plurals ("pools", "balconies"). */
const tagForms = (term: string): string[] => {
  const base = compact(term);
  return /[^aeiou]y$/.test(base)
    ? [base, `${base.slice(0, -1)}ies`]
    : [base, `${base}s`, `${base}es`];
};

/**
 * How many of the asked preferences a hotel meets. Unknown keys are ignored,
 * so a newer caller's vocabulary never breaks an older core.
 */
export const hotelPreferenceHits = (
  preferences: readonly string[],
  facts: HotelFacts
): number => {
  if (preferences.length === 0) return 0;
  const text = plain(
    [
      facts.title,
      facts.description,
      facts.subcategory,
      ...(facts.facilities ?? []),
      ...(facts.outdoorFacilities ?? []),
    ]
      .filter((part): part is string => Boolean(part))
      // Each field is its own clause: a trailing "no smoking" never negates
      // the next facility.
      .join('. ')
  );
  const tags = (facts.hashtags ?? []).map(compact).filter(Boolean);
  let hits = 0;
  for (const entry of HOTEL_PREFERENCE_VOCAB) {
    if (!preferences.includes(entry.key)) continue;
    const met =
      // The hotel's own text is read like the notes: "children are not
      // accepted" is no family hit.
      // Plurals count ("three pools"); Portuguese "no" is not a negation.
      entry.match.some((term) => mentions(text, term, HOTEL_READING)) ||
      entry.match.some((term) =>
        tagForms(term).some((form) => tags.includes(form))
      ) ||
      (entry.key === 'luxury' && (facts.stars ?? 0) >= 5);
    if (met) hits += 1;
  }
  return Math.min(hits, HOTEL_PREFERENCE_MAX_HITS);
};
